// Shared utilities for the mobile UI.

import type { TFunction } from "i18next"
import { formatVolume } from "@/lib/format"
import type { MobileAsset } from "./types"

/**
 * Read the CSRF token for an X-CSRF-Token header. Prefers the `XSRF-TOKEN`
 * cookie that inertia_rails sets on every response — it stays fresh across
 * Inertia navigations and survives pages whose layout suppresses the
 * `csrf_meta_tags` helper (Rodauth-rendered routes do this, which broke the
 * PWA scan flow). Falls back to the meta tag for completeness.
 */
export function csrfToken(): string {
  if (typeof document === "undefined") return ""
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  if (match) {
    try {
      return decodeURIComponent(match[1])
    } catch {
      return match[1]
    }
  }
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
  )
}

/**
 * Render a timestamp as a relative phrase (e.g. "há 4 min", "há 2 h"). Falls
 * back to a localized date for anything older than a week so we don't show
 * "há 412 d". Strings come from `mobile.timeAgo.*` so locale switches work.
 */
export function timeAgo(iso: string | null | undefined, t: TFunction): string {
  if (!iso) return ""
  const ts = new Date(iso)
  if (Number.isNaN(ts.getTime())) return ""
  const seconds = Math.max(0, Math.floor((Date.now() - ts.getTime()) / 1000))
  if (seconds < 60) return t("mobile.timeAgo.now")
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t("mobile.timeAgo.minutes", { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t("mobile.timeAgo.hours", { count: hours })
  const days = Math.floor(hours / 24)
  if (days < 7) return t("mobile.timeAgo.days", { count: days })
  return ts.toLocaleDateString()
}

/**
 * Fields worth showing next to a scanned asset's code, per asset type. The
 * operator scans a batch and needs to confirm at a glance that each asset
 * carries what it should — for a keg that is above all the product, which is
 * why it comes first and in the row's strong ink.
 *
 * Empty values are dropped rather than rendered as a dash: the line has to fit
 * one row on a 360px screen, and a placeholder-heavy line reads as noise. The
 * single exception is a keg with no product — an unassigned keg is itself
 * something the operator needs to catch, so it renders muted instead of
 * vanishing.
 */
export interface AssetSummarySegment {
  text: string
  /** Rendered in the row's strong ink — the one field the operator checks first. */
  emphasis?: boolean
  /** Dimmed and italic — the "no product" placeholder, not a real value. */
  muted?: boolean
  /**
   * How readily this segment gives up width, as a flex-shrink weight. 0 (the
   * default) never truncates: measurements and codes are short and
   * fixed-width, and losing "CO2-5512" to save the tail of a name is the wrong
   * trade. Only free-text names, which are unbounded in length, shrink.
   *
   * The weights are ordered, not just on/off. Left to flexbox's proportional
   * shrinking, a long customer name would drag a short product down with it —
   * "Weiss Bávara" became "Weiss …" next to a 38-character customer. The
   * product is the field the operator scans to verify, so it yields last.
   */
  shrink?: number
}

/**
 * Yields first — a customer name is context, not the thing being checked. The
 * gap to SHRINK_PRIMARY has to be this wide because flexbox splits the
 * overflow proportionally in a single pass: at a weight of 8 the product still
 * absorbed ~3px of a 65px overflow, and 3px is a dropped ellipsis. At 1000 its
 * share rounds to nothing, so the customer collapses fully before the product
 * gives up its first character.
 */
const SHRINK_SECONDARY = 1000
/** Yields only once everything else is exhausted. */
const SHRINK_PRIMARY = 1

export function assetSummarySegments(asset: MobileAsset, t: TFunction): AssetSummarySegment[] {
  const seg = (
    text: string | null | undefined,
    extra: Omit<AssetSummarySegment, "text"> = {},
  ): AssetSummarySegment[] => {
    const trimmed = text?.trim()
    return trimmed ? [{ text: trimmed, ...extra }] : []
  }

  const makeAndModel = [asset.brand, asset.model]
    .map((v) => v?.trim())
    .filter(Boolean)
    .join(" ")

  switch (asset.assetable_type) {
    case "Keg":
      return [
        ...(asset.product?.name?.trim()
          ? seg(asset.product.name, { emphasis: true, shrink: SHRINK_PRIMARY })
          : [{ text: t("mobile.scan.noProduct"), muted: true }]),
        ...seg(asset.customer?.name, { shrink: SHRINK_SECONDARY }),
      ]
    case "Fridge":
      return [...seg(makeAndModel, { shrink: SHRINK_PRIMARY }), ...seg(asset.serial_number)]
    case "Cylinder":
      return [...seg(formatCapacity(asset.capacity, t)), ...seg(asset.serial_number)]
    case "BeerTap":
      return [
        ...seg(makeAndModel, { shrink: SHRINK_PRIMARY }),
        ...seg(asset.tap_count ? t("mobile.scan.taps", { count: asset.tap_count }) : null),
      ]
  }
}

/** Capacity carries no unit anywhere in the app, so it needs its own label. */
function formatCapacity(
  capacity: number | string | null | undefined,
  t: TFunction,
): string | null {
  if (capacity === null || capacity === undefined || capacity === "") return null
  const parsed = typeof capacity === "number" ? capacity : parseFloat(capacity)
  if (Number.isNaN(parsed)) return null
  return `${t("mobile.scan.capacityShort")} ${parsed.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}`
}

/**
 * Fields for a scanned row's expanded panel — everything the one-line summary
 * cannot carry. Code and stage are deliberately absent: they sit in the row
 * header directly above and never truncate, so repeating them would be noise.
 * Product and customer DO repeat, because the line may have trimmed them and
 * seeing them whole is the reason the panel exists.
 *
 * Empty values are dropped, same rule as the summary line.
 */
export interface AssetDetailField {
  label: string
  value: string
  mono?: boolean
  /** Free-text names, shown in full — they wrap rather than ellipsize. */
  wrap?: boolean
}

export function assetDetailFields(asset: MobileAsset, t: TFunction): AssetDetailField[] {
  const field = (
    key: string,
    value: string | null | undefined,
    extra: Omit<AssetDetailField, "label" | "value"> = {},
  ): AssetDetailField[] => {
    const trimmed = value?.trim()
    return trimmed ? [{ label: t(`mobile.assetDetail.fields.${key}`), value: trimmed, ...extra }] : []
  }

  const date = (iso: string | null | undefined): string | null => {
    if (!iso) return null
    // Date-only columns arrive as "2026-09-12"; anchoring to midnight keeps
    // them from sliding a day backwards in negative-offset timezones.
    const parsed = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso)
    return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString()
  }

  const decimal = (value: number | string | null | undefined): string | null => {
    if (value === null || value === undefined || value === "") return null
    const parsed = typeof value === "number" ? value : parseFloat(value)
    return Number.isNaN(parsed) ? null : parsed.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
  }

  const makeAndModel = [asset.brand, asset.model]
    .map((v) => v?.trim())
    .filter(Boolean)
    .join(" ")

  const typeFields = (): AssetDetailField[] => {
    switch (asset.assetable_type) {
      case "Keg":
        // No supplier here: for a keg it is not what the operator is checking,
        // and the volume it displaces is. The summary line carries only the
        // product and customer, so this is the only place volume appears.
        return [
          ...field("product", asset.product?.name, { wrap: true }),
          ...field("container", asset.container?.name),
          ...field("volume", formatVolume(asset.volume)),
          ...field("batchNumber", asset.batch_number, { mono: true }),
          ...field("expirationDate", date(asset.expiration_date)),
        ]
      case "Fridge":
        return [
          ...field("brandModel", makeAndModel, { wrap: true }),
          ...field("serialNumber", asset.serial_number, { mono: true }),
          ...field("capacity", decimal(asset.capacity)),
          ...field("supplier", asset.supplier?.name, { wrap: true }),
        ]
      case "Cylinder":
        return [
          ...field("serialNumber", asset.serial_number, { mono: true }),
          ...field("capacity", decimal(asset.capacity)),
          ...field("pressureRating", decimal(asset.pressure_rating)),
          ...field("lastInspection", date(asset.last_inspection_date)),
          ...field("expirationDate", date(asset.expiry_date)),
          ...field("supplier", asset.supplier?.name, { wrap: true }),
        ]
      case "BeerTap":
        return [
          ...field("brandModel", makeAndModel, { wrap: true }),
          ...field("serialNumber", asset.serial_number, { mono: true }),
          ...field("tapCount", asset.tap_count ? String(asset.tap_count) : null),
          ...field("supplier", asset.supplier?.name, { wrap: true }),
        ]
    }
  }

  return [
    ...typeFields(),
    ...field("customer", asset.customer?.name, { wrap: true }),
    ...field("currentLocation", asset.location?.name, { wrap: true }),
  ]
}
